/**
 * This device's push state, and the sign-out cleanup that reads it.
 *
 * SPLIT OUT OF push.ts (PR #1242 refute round, P2, proved in Chromium). That
 * PR moved firebase/messaging off the first-load path by making every route
 * to push.ts dynamic, sign-out included: auth.ts did `await import('./push')`
 * before detaching this device's token. A browser caches a FAILED module
 * fetch, so once the push chunk failed to load one time (PushSync asks for
 * it on every signed-in load), every later import of it failed too, with no
 * new request, and sign-out refused for the rest of that tab's life. On a
 * shared iPad, staying signed in is the harm. So the removal lives here, in a
 * module auth.ts imports STATICALLY, and sign-out never waits on a chunk.
 *
 * NEVER IMPORT firebase/messaging HERE, directly or through anything this
 * file imports. Everything below runs on Firestore, Auth, localStorage, the
 * notify Function's release call and IndexedDB (pushPreview.ts), all of
 * which are already in the first-load set. tests/push-device-state.test.mjs
 * walks this file's import graph and fails if messaging ever appears in it,
 * and tests/harness/sign-out-push-chunk/check.mjs proves sign-out completes,
 * and really detaches the token, with the push chunk failing.
 *
 * ONE SOURCE OF TRUTH. push.ts imports this state from here and never keeps
 * a copy: the signing-out set, the per-account operation epochs, the
 * in-flight token writes, the consent marker and the local token keys. A
 * second copy would let an enrollment that started before sign-out miss the
 * sign-out and re-enrol the device for the account that just left it.
 */
import { arrayRemove, doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './firebase';
import { releasePushTokenOwnership } from './pushTokenClaim';
import { clearPushPreviewIdentity } from './pushPreview';

// Remember THIS device's token so sign-out can remove exactly it from the
// user doc - otherwise a shared family iPad keeps the last user's token and
// pushes their notifications to whoever signs in next.
export const DEVICE_TOKEN_KEY = 'ldg_fcm_device_token';
export const DEVICE_CLAIM_KEY = 'ldg_fcm_device_claim';
const PENDING_RELEASE_KEY = 'ldg_fcm_pending_release';

export function localClaimId(): string | null {
  try { return window.localStorage?.getItem(DEVICE_CLAIM_KEY) ?? null; }
  catch { return null; }
}

// The localStorage GETTER throws SecurityError when the browser blocks
// storage (Safari "Block All Cookies"), and `?.` does not stop a throwing
// getter. Every caller has just read the token successfully, so this only
// guards a block that begins in between; clearing is best effort then.
// (Sign-out itself fails closed earlier, at its own read of the token.)
export function forgetLocalDeviceToken(): void {
  try {
    window.localStorage?.removeItem(DEVICE_TOKEN_KEY);
    window.localStorage?.removeItem(DEVICE_CLAIM_KEY);
  } catch { /* Best effort, as above. */ }
}

/**
 * The token this browser last stored, or null. Lets a screen check whether
 * THIS device is genuinely on the user's doc rather than trusting the
 * permission flag, which says nothing about whether a token was ever saved.
 */
export function currentDeviceToken(): string | null {
  try {
    return window.localStorage?.getItem(DEVICE_TOKEN_KEY) ?? null;
  } catch {
    return null;
  }
}

interface ActivePushWrite {
  token: string;
  work: Promise<void>;
}

const activePushWrites = new Map<string, Set<ActivePushWrite>>();
const signingOutPushUids = new Set<string>();
const pushOperationEpochs = new Map<string, number>();
const PUSH_CLEANUP_TIMEOUT_MS = 1_500;

/** True from the moment sign-out starts detaching this account's device. */
export function isSigningOutPush(uid: string): boolean {
  return signingOutPushUids.has(uid);
}

export function currentPushOperationEpoch(uid: string): number {
  return pushOperationEpochs.get(uid) ?? 0;
}

export function invalidatePushOperations(uid: string): void {
  pushOperationEpochs.set(uid, currentPushOperationEpoch(uid) + 1);
}

export async function completesWithin(work: Promise<unknown>, timeoutMs = PUSH_CLEANUP_TIMEOUT_MS): Promise<boolean> {
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  try {
    return await Promise.race([
      work.then(() => true, () => false),
      new Promise<boolean>((resolve) => {
        timer = globalThis.setTimeout(() => resolve(false), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) globalThis.clearTimeout(timer);
  }
}

export async function resolvesTrueWithin(work: Promise<boolean>, timeoutMs = PUSH_CLEANUP_TIMEOUT_MS): Promise<boolean> {
  let timer: ReturnType<typeof globalThis.setTimeout> | undefined;
  try {
    return await Promise.race([
      work.then((value) => value === true, () => false),
      new Promise<boolean>((resolve) => {
        timer = globalThis.setTimeout(() => resolve(false), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) globalThis.clearTimeout(timer);
  }
}

export interface PendingPushRelease {
  token: string;
  claimId: string | null;
  allowUnclaimed?: boolean;
}

export function pendingRelease(): PendingPushRelease | null {
  try {
    const raw = window.localStorage?.getItem(PENDING_RELEASE_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Partial<PendingPushRelease>;
      return typeof parsed.token === 'string'
        ? { token: parsed.token, claimId: typeof parsed.claimId === 'string' ? parsed.claimId : null,
          allowUnclaimed: parsed.allowUnclaimed === true }
        : null;
    } catch {
      // One unreleased candidate build stored only the token string.
      return { token: raw, claimId: null };
    }
  } catch {
    return null;
  }
}

export function rememberPendingRelease(token: string, claimId: string | null, allowUnclaimed = false): void {
  try {
    window.localStorage?.setItem(PENDING_RELEASE_KEY, JSON.stringify({ token, claimId, allowUnclaimed }));
  } catch {
    // The generic server payload still prevents account-private disclosure.
  }
}

export function clearPendingRelease(token: string): void {
  try {
    if (pendingRelease()?.token === token) {
      window.localStorage.removeItem(PENDING_RELEASE_KEY);
    }
  } catch {
    // best-effort
  }
}

export function trackPushWrite(uid: string, token: string, work: Promise<void>): ActivePushWrite {
  const record = { token, work };
  const set = activePushWrites.get(uid) ?? new Set<ActivePushWrite>();
  set.add(record);
  activePushWrites.set(uid, set);
  void work.finally(() => {
    set.delete(record);
    if (set.size === 0) activePushWrites.delete(uid);
  }).catch(() => {});
  return record;
}

/**
 * Per-account proof that THIS account opted in ON THIS DEVICE.
 *
 * Browser notification permission is origin-wide and outlives sign-out, so
 * "permission is granted" says nothing about who granted it. Without this
 * marker the silent start-up refresh would enrol whoever signs in NEXT on a
 * shared machine: the front desk computer, a studio tablet, a parent
 * borrowing a teacher's phone. They would be opted into push having never
 * agreed to anything. The key is scoped to the uid so a second account can
 * never inherit the first account's consent.
 */
function enrolledMarkerKey(uid: string): string {
  return `ldg_fcm_enrolled:${uid}`;
}

export function markEnrolledHere(uid: string): void {
  try {
    window.localStorage?.setItem(enrolledMarkerKey(uid), '1');
  } catch {
    // Private mode. The refresh simply will not run, which fails toward
    // "ask again" rather than toward enrolling someone silently.
  }
}

/**
 * Whether THIS account already opted into push ON THIS DEVICE - the loud
 * opt-in popup (PushOptInGate) hides itself on it. Exported name says what
 * callers mean; the internal helpers keep their historical names.
 */
export function isEnrolledOnThisDevice(uid: string): boolean {
  return hasEnrolledHere(uid);
}

export function hasEnrolledHere(uid: string): boolean {
  try {
    return window.localStorage?.getItem(enrolledMarkerKey(uid)) === '1';
  } catch {
    return false;
  }
}

export function clearEnrolledHere(uid: string): void {
  try {
    window.localStorage?.removeItem(enrolledMarkerKey(uid));
  } catch {
    // best-effort
  }
}

/**
 * Remove THIS device's FCM token from the signed-in user's doc. Call right
 * before sign-out so the next user on a shared device does not inherit the
 * previous user's push registration. It waits for an enrollment already in
 * flight, then detaches every token that operation could have created.
 */
export async function removeCurrentDeviceToken(): Promise<void> {
  const uid = getAuth().currentUser?.uid;
  // Drop the consent marker FIRST, and unconditionally. Leaving this device
  // is the moment the opt-in ends, so the silent refresh must not fire for
  // this account on the next sign-in whatever else happens below. It is
  // scoped to this uid, so nobody else's marker is touched and no other
  // account can inherit this one.
  if (!uid) return;
  // Private preview authority disappears before any network cleanup starts.
  // A slow Firestore or FCM release can therefore never leave old message
  // text visible after the person has begun signing out.
  const previewCleanup = clearPushPreviewIdentity(null, uid);
  invalidatePushOperations(uid);
  signingOutPushUids.add(uid);
  clearEnrolledHere(uid);

  const active = [...(activePushWrites.get(uid) ?? [])];
  await Promise.all([
    completesWithin(Promise.allSettled(active.map((record) => record.work))),
    previewCleanup,
  ]);
  // An in-flight explicit opt-in may have written the marker just before it
  // observed signingOutPushUids. Clear it again after the join.
  clearEnrolledHere(uid);
  // An in-flight claim may have staged a newer generation just before it saw
  // the sign-out epoch. Clear once more after joining that work.
  await clearPushPreviewIdentity(null, uid);

  // FAIL CLOSED, on purpose: this read is NOT the guarded currentDeviceToken().
  // When the browser blocks storage the getter throws, and that throw must
  // reach signOut(). A token saved before storage was blocked is still on
  // this account's doc and still owned by it, nothing here can find it to
  // detach it, and signing out anyway would leave this device receiving the
  // account's notifications (Codex review, PR #1218). signOut() turns the
  // throw into its refusal: the person stays signed in and is told this
  // device could not be disconnected. The harness pins this refusal.
  const localToken = window.localStorage?.getItem(DEVICE_TOKEN_KEY) ?? null;
  const tokens = [...new Set([
    ...(localToken ? [localToken] : []),
    ...active.map((record) => record.token),
  ])];
  if (tokens.length === 0) return;

  const claimHere = localClaimId();
  const serverDetach = db
    ? updateDoc(doc(db, 'users', uid), {
        fcmTokens: arrayRemove(...tokens),
        updatedAt: serverTimestamp(),
      })
    : Promise.reject(new Error('Firestore unavailable'));
  const releases = tokens.map((token) => resolvesTrueWithin(
    releasePushTokenOwnership(token, token === localToken ? claimHere : null),
  ));
  const [serverDetached, releaseResults] = await Promise.all([
    completesWithin(serverDetach),
    Promise.allSettled(releases),
  ]);
  // A stalled pre-claim write can finish after this bounded cleanup. Its
  // invalidated enrollment operation owns the matching follow-up removal.
  // Remember the local generation when neither immediate server path worked.
  const authoritativeReleaseCompleted = releaseResults.some(
    (result) => result.status === 'fulfilled' && result.value === true,
  );
  if (!serverDetached && !authoritativeReleaseCompleted && localToken) {
    rememberPendingRelease(localToken, claimHere);
  }
  forgetLocalDeviceToken();
}

/**
 * Let the current account use push again when sign-out was cancelled before
 * Firebase Auth changed users. A successful sign-out reloads the app, so only
 * the failure path needs to clear this in-memory guard.
 */
export function cancelCurrentDeviceTokenRemoval(): void {
  const uid = getAuth().currentUser?.uid;
  if (uid) signingOutPushUids.delete(uid);
}
